import AccountTree from "@mui/icons-material/AccountTree";
import ArrowBack from "@mui/icons-material/ArrowBack";
import ArrowDownward from "@mui/icons-material/ArrowDownward";
import Block from "@mui/icons-material/Block";
import Bolt from "@mui/icons-material/Bolt";
import Close from "@mui/icons-material/Close";
import Lock from "@mui/icons-material/Lock";
import PersonAdd from "@mui/icons-material/PersonAdd";
import PersonRemove from "@mui/icons-material/PersonRemove";
import PushPin from "@mui/icons-material/PushPin";
import Visibility from "@mui/icons-material/Visibility";
import {
  Alert,
  Avatar,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  FormControlLabel,
  IconButton,
  List,
  ListItemButton,
  ListItemText,
  Radio,
  RadioGroup,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { useState } from "react";
import { toast } from "react-hot-toast";
import { programPath } from "../../program-tree/program-tree";
import {
  accessSource,
  contributorAccess,
  descendantsOf,
  isListed,
  levelLabels,
  plural,
  removeText,
} from "../access-model";
import { useRemoveProgramAccess, useSetProgramAccess } from "../hooks/use-program-access";
import { ContributorSubfolders } from "./contributor-subfolders";
import { LevelSelect } from "./level-select";

import type { ProgramNode, ProgramTree } from "../../program-tree/program-tree";
import type { ContributorAccess, Level } from "../access-model";
import type { AccessData, AccessPerson } from "../hooks/use-program-access";
import type { FC, ReactElement } from "react";

const sourceIcons: Record<ReturnType<typeof accessSource>["kind"], ReactElement> = {
  set: <PushPin sx={{ fontSize: 16 }} />,
  blocked: <Block sx={{ fontSize: 16 }} />,
  inherited: <ArrowDownward sx={{ fontSize: 16 }} />,
  "name-only": <Visibility sx={{ fontSize: 16 }} />,
};

const sourceColours: Record<ReturnType<typeof accessSource>["kind"], string> = {
  set: "primary.main",
  blocked: "error.main",
  inherited: "text.secondary",
  "name-only": "warning.dark",
};

/** The contributors listed on a program, with their access. */
export const listedContributors = (data: AccessData, tree: ProgramTree, program: ProgramNode) =>
  data.contributors
    .map((person) => ({
      person,
      access: contributorAccess(data.index, tree, person.id, program.id),
    }))
    .filter(({ access }) => isListed(access));

/** A contributor's level on the program, where it comes from, and what can be done with it. */
const ContributorRow: FC<{
  person: AccessPerson;
  access: ContributorAccess;
  tree: ProgramTree;
  program: ProgramNode;
  descendantCount: number;
  busy: boolean;
  onLevel: (level: Level) => void;
  onSubfolders: () => void;
  onRemove: () => void;
}> = ({
  person,
  access,
  tree,
  program,
  descendantCount,
  busy,
  onLevel,
  onSubfolders,
  onRemove,
}) => {
  const source = accessSource(access, tree, descendantCount);

  return (
    <Box component="li">
      <Stack direction="row" spacing={1.5} sx={{ alignItems: "center" }}>
        <Avatar sx={{ width: 32, height: 32, fontSize: 13 }}>{person.initials}</Avatar>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography variant="body2" sx={{ fontWeight: 600 }} noWrap>
            {person.name}
          </Typography>
          <Typography variant="caption" sx={{ color: "text.secondary" }} noWrap>
            {person.email}
          </Typography>
        </Box>
        <LevelSelect
          label={`Access level for ${person.name} in ${program.title}`}
          level={access.access.level}
          disabled={busy}
          onChange={onLevel}
        />
      </Stack>
      <Stack direction="row" spacing={1} sx={{ alignItems: "center", pl: 5.5, mt: 0.5 }}>
        <Typography
          variant="caption"
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 0.5,
            color: sourceColours[source.kind],
            flex: 1,
          }}
        >
          {sourceIcons[source.kind]}
          {source.text}
        </Typography>
        {descendantCount ? (
          <Button
            size="small"
            startIcon={<AccountTree />}
            onClick={onSubfolders}
            aria-label={`Subfolders for ${person.name}${
              access.exceptions ? `, ${plural(access.exceptions, "exception")}` : ""
            }`}
            data-analytics-id="program-access-subfolders"
          >
            {access.exceptions
              ? `Subfolders · ${plural(access.exceptions, "exception")}`
              : "Subfolders"}
          </Button>
        ) : null}
        {access.access.setLevel !== null || access.access.level === "none" ? (
          <IconButton
            size="small"
            aria-label={`Remove ${person.name}`}
            disabled={busy}
            onClick={onRemove}
            data-analytics-id="program-access-remove"
          >
            <PersonRemove fontSize="small" />
          </IconButton>
        ) : null}
      </Stack>
    </Box>
  );
};

/** The most people the add form lists, so that a large account does not list each employee. */
const MAX_MATCHES = 50;

/** Gives a person who has no access here a level on the program, which makes them a contributor. */
const AddContributorForm: FC<{
  program: ProgramNode;
  candidates: AccessPerson[];
  descendantCount: number;
  busy: boolean;
  onCancel: () => void;
  onAdd: (person: AccessPerson, level: Level) => void;
}> = ({ program, candidates, descendantCount, busy, onCancel, onAdd }) => {
  const [query, setQuery] = useState("");
  const [person, setPerson] = useState<AccessPerson | null>(null);
  const [level, setLevel] = useState<Level | null>(null);
  const search = query.trim().toLowerCase();
  const matches = candidates.filter(
    ({ name, email }) =>
      !search || name.toLowerCase().includes(search) || email.toLowerCase().includes(search),
  );

  return (
    <Stack spacing={2} sx={{ p: 2, mb: 2, border: 1, borderColor: "divider", borderRadius: 1 }}>
      <TextField
        autoFocus
        size="small"
        type="search"
        label="Search people by name or email"
        value={query}
        onChange={(e) => setQuery(e.target.value)}
      />
      <List dense aria-label="Matching people" sx={{ maxHeight: 200, overflowY: "auto" }}>
        {matches.slice(0, MAX_MATCHES).map((match) => (
          <ListItemButton
            key={match.id}
            selected={person?.id === match.id}
            aria-pressed={person?.id === match.id}
            onClick={() => setPerson(match)}
          >
            <Avatar sx={{ width: 28, height: 28, fontSize: 12, mr: 1.5 }}>{match.initials}</Avatar>
            <ListItemText primary={match.name} secondary={match.email} />
          </ListItemButton>
        ))}
        {matches.length === 0 ? (
          <Typography variant="body2" sx={{ color: "text.secondary", px: 2 }}>
            No people match that search.
          </Typography>
        ) : null}
        {matches.length > MAX_MATCHES ? (
          <Typography variant="body2" sx={{ color: "text.secondary", px: 2 }}>
            {`Showing ${MAX_MATCHES} of ${matches.length}. Search to find others.`}
          </Typography>
        ) : null}
      </List>
      <RadioGroup
        aria-label="Access level"
        value={level ?? ""}
        onChange={(e) => setLevel(e.target.value === "edit" ? "edit" : "read")}
      >
        <FormControlLabel
          value="read"
          control={<Radio />}
          label={`${levelLabels.read}: can see this program and everything in it.`}
        />
        <FormControlLabel
          value="edit"
          control={<Radio />}
          label={`${levelLabels.edit}: can see, edit and create anything in this program.`}
        />
      </RadioGroup>
      <Typography
        variant="body2"
        sx={{ display: "flex", alignItems: "center", gap: 1, color: "text.secondary" }}
      >
        <AccountTree fontSize="small" />
        {descendantCount
          ? `Applies to ${program.title} and all ${plural(descendantCount, "subfolder")} inside it. You can change individual subfolders afterwards.`
          : `Applies to ${program.title}. Any subfolders added later will inherit it.`}
      </Typography>
      <Stack direction="row" spacing={1} sx={{ justifyContent: "flex-end" }}>
        <Button onClick={onCancel} data-analytics-id="program-access-add-cancel">
          Cancel
        </Button>
        <Button
          variant="contained"
          disabled={!person || !level || busy}
          onClick={() => {
            if (person && level) onAdd(person, level);
          }}
          data-analytics-id="program-access-add-save"
        >
          Add contributor
        </Button>
      </Stack>
    </Stack>
  );
};

/**
 * Who can see and change a program and the programs below it. Admins always have Create and edit. A
 * contributor's level is set on a program, or comes from the nearest program above it. Each change
 * is saved straight away.
 */
export const AccessDialog: FC<{
  open: boolean;
  onClose: () => void;
  tree: ProgramTree;
  program: ProgramNode;
  data: AccessData;
  reload: () => void;
}> = ({ open, onClose, tree, program, data, reload }) => {
  const setProgramAccess = useSetProgramAccess();
  const removeProgramAccess = useRemoveProgramAccess();
  const [busy, setBusy] = useState(false);
  const [flash, setFlash] = useState<string | null>(null);
  const [subfoldersOf, setSubfoldersOf] = useState<AccessPerson | null>(null);
  const [removing, setRemoving] = useState<AccessPerson | null>(null);
  const [adding, setAdding] = useState(false);

  const descendantCount = descendantsOf(tree, program.id).length;
  const listed = listedContributors(data, tree, program);
  const listedIds = new Set(listed.map(({ person }) => person.id));
  const candidates = data.people.filter(({ id }) => !listedIds.has(id));

  const save = async (change: () => Promise<void>, done?: string): Promise<boolean> => {
    setBusy(true);
    try {
      await change();
      setFlash(done ?? null);
      return true;
    } catch {
      toast.error("The access could not be changed.");
      return false;
    } finally {
      setBusy(false);
      reload();
    }
  };

  const close = (): void => {
    setSubfoldersOf(null);
    setAdding(false);
    setFlash(null);
    onClose();
  };

  const startAdd = (): void => {
    setAdding(true);
    setFlash(null);
  };

  const add = async (person: AccessPerson, level: Level): Promise<void> => {
    const saved = await save(
      () => setProgramAccess(person.id, program.id, level),
      `${person.name} now has ${levelLabels[level]} on ${program.title}${
        descendantCount ? ` and its ${plural(descendantCount, "subfolder")}` : ""
      }.`,
    );
    if (saved) setAdding(false);
  };

  const removingAccess = removing
    ? removeText(
        contributorAccess(data.index, tree, removing.id, program.id),
        tree,
        program,
        removing.name,
        descendantCount,
      )
    : null;

  return (
    <>
      <Dialog open={open} onClose={close} fullWidth maxWidth="sm" aria-labelledby="access-title">
        {subfoldersOf ? (
          <DialogTitle
            id="access-title"
            sx={{ display: "flex", alignItems: "center", gap: 1, pr: 7 }}
          >
            <IconButton
              aria-label="Back to Access"
              onClick={() => setSubfoldersOf(null)}
              data-analytics-id="program-access-back"
            >
              <ArrowBack />
            </IconButton>
            <Box>
              {subfoldersOf.name}
              <Typography variant="body2" sx={{ color: "text.secondary" }}>
                Access in {program.title}
              </Typography>
            </Box>
          </DialogTitle>
        ) : (
          <DialogTitle id="access-title" sx={{ pr: 7 }}>
            Access
            <Typography variant="body2" sx={{ color: "text.secondary" }}>
              {programPath(tree, program.id)
                .map(({ title }) => title)
                .join(" › ")}
            </Typography>
          </DialogTitle>
        )}
        <IconButton
          aria-label="Close"
          onClick={close}
          sx={{ position: "absolute", right: 8, top: 8 }}
          data-analytics-id="program-access-close"
        >
          <Close />
        </IconButton>

        <DialogContent dividers>
          {subfoldersOf ? (
            <ContributorSubfolders
              tree={tree}
              program={program}
              person={subfoldersOf}
              index={data.index}
              busy={busy}
              onLevel={(node, level) => {
                void save(() => setProgramAccess(subfoldersOf.id, node.id, level));
              }}
            />
          ) : (
            <Stack spacing={3}>
              <Box component="section" aria-labelledby="access-admins">
                <Stack direction="row" spacing={1} sx={{ alignItems: "center" }}>
                  <Typography id="access-admins" variant="subtitle1" component="h3">
                    Admins
                  </Typography>
                  <Typography
                    variant="caption"
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 0.5,
                      color: "text.secondary",
                    }}
                  >
                    <Lock sx={{ fontSize: 14 }} />
                    {levelLabels.edit}
                  </Typography>
                </Stack>
                <Typography variant="body2" sx={{ color: "text.secondary" }}>
                  Admins always have Create and edit access to every program and sub-program. This
                  can’t be changed.
                </Typography>
                <Typography variant="body2" sx={{ mt: 1 }}>
                  {data.admins.map(({ name }) => name).join(", ")}
                </Typography>
              </Box>

              <Box component="section" aria-labelledby="access-contributors">
                <Stack
                  direction="row"
                  sx={{ alignItems: "center", justifyContent: "space-between", mb: 1 }}
                >
                  <Typography id="access-contributors" variant="subtitle1" component="h3">
                    Contributors
                  </Typography>
                  {adding ? null : (
                    <Button
                      variant="contained"
                      size="small"
                      startIcon={<PersonAdd />}
                      onClick={startAdd}
                      data-analytics-id="program-access-add-contributor"
                    >
                      Add contributor
                    </Button>
                  )}
                </Stack>
                {program.parent_id ? null : (
                  <Typography variant="body2" sx={{ color: "text.secondary", mb: 1 }}>
                    Contributors have no access to a new program until you add them here.
                  </Typography>
                )}
                {flash ? (
                  <Alert severity="success" role="status" sx={{ mb: 1 }}>
                    {flash}
                  </Alert>
                ) : null}

                {adding ? (
                  <AddContributorForm
                    program={program}
                    candidates={candidates}
                    descendantCount={descendantCount}
                    busy={busy}
                    onCancel={() => setAdding(false)}
                    onAdd={(person, level) => {
                      void add(person, level);
                    }}
                  />
                ) : null}

                <Stack component="ul" spacing={1.5} sx={{ listStyle: "none", m: 0, p: 0 }}>
                  {listed.map(({ person, access }) => (
                    <ContributorRow
                      key={person.id}
                      person={person}
                      access={access}
                      tree={tree}
                      program={program}
                      descendantCount={descendantCount}
                      busy={busy}
                      onLevel={(level) => {
                        void save(() => setProgramAccess(person.id, program.id, level));
                      }}
                      onSubfolders={() => {
                        setFlash(null);
                        setAdding(false);
                        setSubfoldersOf(person);
                      }}
                      onRemove={() => setRemoving(person)}
                    />
                  ))}
                </Stack>
                {listed.length === 0 && !adding ? (
                  <Typography variant="body2" sx={{ color: "text.secondary" }}>
                    Only admins can see this program. Add a contributor to share it.
                  </Typography>
                ) : null}
              </Box>
            </Stack>
          )}
        </DialogContent>

        <DialogActions sx={{ justifyContent: "space-between", px: 3 }}>
          <Typography
            variant="caption"
            sx={{ display: "flex", alignItems: "center", gap: 0.5, color: "text.secondary" }}
          >
            <Bolt sx={{ fontSize: 16 }} />
            Changes to access apply straight away.
          </Typography>
          <Button variant="contained" onClick={close} data-analytics-id="program-access-done">
            Done
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={Boolean(removing)}
        onClose={() => setRemoving(null)}
        role="alertdialog"
        aria-labelledby="remove-access-title"
        aria-describedby="remove-access-body"
      >
        <DialogTitle id="remove-access-title">{removingAccess?.title}</DialogTitle>
        <DialogContent>
          <DialogContentText id="remove-access-body">{removingAccess?.body}</DialogContentText>
          {removingAccess?.note ? (
            <DialogContentText sx={{ mt: 1 }}>{removingAccess.note}</DialogContentText>
          ) : null}
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setRemoving(null)}
            data-analytics-id="program-access-remove-cancel"
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            color="error"
            disabled={busy}
            data-analytics-id="program-access-remove-confirm"
            onClick={() => {
              const person = removing;
              setRemoving(null);
              if (person)
                void save(
                  () => removeProgramAccess(person.id, program.id),
                  `${person.name} was removed from ${program.title}${
                    descendantCount ? " and its subfolders" : ""
                  }.`,
                );
            }}
          >
            Remove access
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};
